import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, Loader2, AlertCircle, CheckCircle2, Lock, ShieldCheck } from 'lucide-react'
import { ROUTES } from '@/router/routes'
import { authApi } from '@/modules/auth/api/auth.api'

const schema = z
  .object({
    password: z.string().min(8, 'Minimum 8 caractères').regex(/[A-Z]/, 'Au moins une majuscule').regex(/[0-9]/, 'Au moins un chiffre').regex(/[^A-Za-z0-9]/, 'Au moins un caractère spécial'),
    confirmPassword: z.string(),
  })
  .refine(d => d.password === d.confirmPassword, { message: 'Les mots de passe ne correspondent pas', path: ['confirmPassword'] })

type FormData = z.infer<typeof schema>

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token') ?? ''
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const { register, handleSubmit, formState: { errors }, watch } = useForm<FormData>({ resolver: zodResolver(schema) })

  const password = watch('password', '')
  const strength = (() => { let s = 0; if (password.length >= 8) s++; if (/[A-Z]/.test(password)) s++; if (/[0-9]/.test(password)) s++; if (/[^A-Za-z0-9]/.test(password)) s++; if (password.length >= 12) s++; return s })()
  const strengthLabel = ['', 'Faible', 'Faible', 'Moyen', 'Fort', 'Très fort']
  const strengthColor = ['', '#EF4444', '#F59E0B', '#F59E0B', '#10B981', '#059669']

  const onSubmit = async (data: FormData) => {
    if (!token) { setError("Token d'invitation manquant."); return }
    setLoading(true); setError(null)
    try {
      await authApi.acceptInvite(token, data.password)
      setSuccess(true)
      setTimeout(() => navigate(ROUTES.LOGIN, { state: { accountActivated: true } }), 2500)
    } catch (err: any) { setError(err?.response?.data?.message ?? "Erreur lors de l'activation.") }
    finally { setLoading(false) }
  }

  const inputStyle = (focused: boolean, hasError: boolean): React.CSSProperties => ({
    width: '100%', height: 48, padding: '0 44px 0 44px',
    borderRadius: 12, border: `1.5px solid ${hasError ? '#EF4444' : focused ? 'var(--brand-orange)' : 'var(--bd)'}`,
    fontSize: 14, color: 'var(--tx)', background: focused ? '#fff' : 'var(--surface-2)',
    outline: 'none', transition: 'all 0.2s', boxShadow: focused ? '0 0 0 3px rgba(245,124,0,0.10)' : 'none',
  })

  if (!token) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'var(--bg)' }}>
      <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 16, padding: '32px 28px', textAlign: 'center', maxWidth: 400 }}>
        <AlertCircle size={32} style={{ color: '#DC2626', margin: '0 auto 12px' }} />
        <p style={{ fontFamily: '"Raleway", sans-serif', fontWeight: 700, fontSize: 16, color: '#991B1B', margin: '0 0 8px' }}>Lien invalide</p>
        <p style={{ fontSize: 13, color: '#B91C1C' }}>Ce lien d'invitation est invalide ou a expiré.</p>
      </div>
    </div>
  )

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'var(--bg)', position: 'relative' }}>
      {/* Top accent bar */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: 'linear-gradient(90deg, var(--brand-orange), var(--brand-amber))' }} />

      <div style={{ width: '100%', maxWidth: 440 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 15, margin: '0 auto 14px',
            background: 'linear-gradient(135deg, var(--brand-orange), var(--brand-amber))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 6px 24px rgba(245,124,0,0.30)',
          }}>
            <span style={{ fontFamily: '"Raleway", sans-serif', fontWeight: 900, fontSize: 24, color: '#fff' }}>M</span>
          </div>
          <h1 style={{ fontFamily: '"Raleway", sans-serif', fontWeight: 800, fontSize: 24, color: 'var(--tx)', letterSpacing: '-0.03em', margin: '0 0 6px' }}>
            Activer votre compte
          </h1>
          <p style={{ fontSize: 14, color: 'var(--tx3)' }}>
            Définissez votre mot de passe pour rejoindre NexusCorp
          </p>
        </div>

        {/* Card */}
        <div style={{
          background: '#fff', borderRadius: 20, padding: '36px 32px',
          border: '1px solid var(--bd)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03), 0 8px 30px rgba(0,0,0,0.06)',
        }}>
          {success ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%', background: '#ECFDF5',
                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
              }}>
                <CheckCircle2 size={28} style={{ color: '#059669' }} />
              </div>
              <p style={{ fontFamily: '"Raleway", sans-serif', fontWeight: 700, fontSize: 18, color: '#065F46', margin: '0 0 6px' }}>Compte activé !</p>
              <p style={{ fontSize: 13, color: 'var(--tx3)' }}>Redirection vers la connexion…</p>
            </div>
          ) : (
            <>
              {error && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 12, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#991B1B' }}>
                  <AlertCircle size={16} style={{ flexShrink: 0 }} /><span style={{ fontWeight: 500 }}>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {/* Password */}
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--tx2)', marginBottom: 8, fontFamily: '"Raleway", sans-serif' }}>Mot de passe</label>
                  <div style={{ position: 'relative' }}>
                    <div style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--tx4)' }}><Lock size={16} /></div>
                    <input {...register('password')} type={showPassword ? 'text' : 'password'} placeholder="Min. 8 caractères" style={inputStyle(false, !!errors.password)}
                      onFocus={e => { e.target.style.borderColor = 'var(--brand-orange)'; e.target.style.boxShadow = '0 0 0 3px rgba(245,124,0,0.10)'; e.target.style.background = '#fff' }}
                      onBlur={e => { e.target.style.borderColor = errors.password ? '#EF4444' : 'var(--bd)'; e.target.style.boxShadow = 'none'; e.target.style.background = 'var(--surface-2)' }}
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--tx4)', padding: 4 }}>
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {/* Strength bar */}
                  {password && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ display: 'flex', gap: 3 }}>
                        {[1,2,3,4,5].map(i => (
                          <div key={i} style={{ flex: 1, height: 4, borderRadius: 99, background: i <= strength ? strengthColor[strength] : 'var(--surface-3)', transition: 'background 0.2s' }} />
                        ))}
                      </div>
                      <p style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 4 }}>Force : <span style={{ fontWeight: 600, color: strengthColor[strength] }}>{strengthLabel[strength]}</span></p>
                    </div>
                  )}
                  {errors.password && <p style={{ fontSize: 12, color: '#DC2626', marginTop: 6 }}>{errors.password.message}</p>}
                </div>

                {/* Confirm */}
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--tx2)', marginBottom: 8, fontFamily: '"Raleway", sans-serif' }}>Confirmer le mot de passe</label>
                  <div style={{ position: 'relative' }}>
                    <div style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--tx4)' }}><ShieldCheck size={16} /></div>
                    <input {...register('confirmPassword')} type={showConfirm ? 'text' : 'password'} placeholder="Retapez le mot de passe" style={inputStyle(false, !!errors.confirmPassword)}
                      onFocus={e => { e.target.style.borderColor = 'var(--brand-orange)'; e.target.style.boxShadow = '0 0 0 3px rgba(245,124,0,0.10)'; e.target.style.background = '#fff' }}
                      onBlur={e => { e.target.style.borderColor = errors.confirmPassword ? '#EF4444' : 'var(--bd)'; e.target.style.boxShadow = 'none'; e.target.style.background = 'var(--surface-2)' }}
                    />
                    <button type="button" onClick={() => setShowConfirm(!showConfirm)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--tx4)', padding: 4 }}>
                      {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {errors.confirmPassword && <p style={{ fontSize: 12, color: '#DC2626', marginTop: 6 }}>{errors.confirmPassword.message}</p>}
                </div>

                {/* Rules checklist */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
                  {[
                    { test: password.length >= 8, label: '8+ caractères' },
                    { test: /[A-Z]/.test(password), label: 'Majuscule' },
                    { test: /[0-9]/.test(password), label: 'Chiffre' },
                    { test: /[^A-Za-z0-9]/.test(password), label: 'Spécial' },
                  ].map(({ test, label }) => (
                    <span key={label} style={{ fontSize: 11.5, display: 'flex', alignItems: 'center', gap: 4, color: test ? '#059669' : 'var(--tx4)', fontWeight: test ? 600 : 400, transition: 'color 0.2s' }}>
                      {test ? '✓' : '○'} {label}
                    </span>
                  ))}
                </div>

                <button type="submit" disabled={loading} className="login-submit-btn" style={{
                  width: '100%', height: 50, borderRadius: 12, border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  fontFamily: '"Raleway", sans-serif', fontWeight: 700, fontSize: 14.5, color: '#fff',
                  background: loading ? 'rgba(245,124,0,0.5)' : 'linear-gradient(135deg, var(--brand-orange), var(--brand-amber))',
                  boxShadow: loading ? 'none' : '0 4px 20px rgba(245,124,0,0.30)',
                  transition: 'all 0.25s', marginTop: 4,
                }}>
                  {loading ? <><Loader2 size={17} className="animate-spin" />Activation…</> : 'Activer mon compte'}
                </button>
              </form>
            </>
          )}
        </div>

        <p style={{ textAlign: 'center', marginTop: 24, fontSize: 11.5, color: 'var(--tx4)', fontFamily: '"Raleway", sans-serif' }}>
          © 2026 NexusCorp · Agence de Communication & Événementiel
        </p>
      </div>
    </div>
  )
}
